import { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { http, formatErr } from "../../lib/api";
import { toast } from "sonner";
import { UserCog, KeyRound, Mail } from "lucide-react";
import { MfaSettings } from "../../components/MfaSettings";

/**
 * Profilo e sicurezza del PLATFORM_ADMIN.
 * Cambio password + cambio email + logout-all.
 */
export default function PlatformProfile() {
  const { user, refresh } = useAuth();
  const [pwOld, setPwOld] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [emPw, setEmPw] = useState("");
  const [emCode, setEmCode] = useState("");
  const [busy, setBusy] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    if (pwNew.length < 12) return toast.error("Nuova password troppo corta (min 12 char).");
    setBusy(true);
    try {
      await http.post("/auth/change-password", { current_password: pwOld, new_password: pwNew });
      setPwOld(""); setPwNew("");
      toast.success("Password aggiornata.");
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/auth/request-email-change", { new_email: newEmail, current_password: emPw, ...(emCode.includes("-") ? { recovery_code: emCode } : { code: emCode }) });
      setEmPw(""); setEmCode("");
      toast.success("Richiesta inviata. Controlla la nuova email per confermare.");
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  const submitLogoutAll = async () => {
    try {
      const r = await http.post("/saloncentral/me/logout-all");
      toast.success(`Sessioni revocate (v=${r.data?.sessions_version_bumped_to}). Rientra.`);
      await (refresh?.() || Promise.resolve());
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div data-testid="platform-profile-page" className="max-w-3xl mx-auto p-4 md:p-6">
      <div className="mb-6">
        <h1 className="font-serif text-3xl">Profilo e sicurezza</h1>
        <p className="text-xs text-neutral-500 mt-1">
          Account centrale: <b>{user?.email}</b> · Ruolo: <b>PLATFORM_ADMIN</b>
        </p>
      </div>

      <section className="card-clean p-5 mb-5" data-testid="platform-profile-password">
        <div className="flex items-center gap-2 mb-3"><KeyRound className="w-4 h-4" /><h2 className="font-serif text-xl">Cambio password</h2></div>
        <form onSubmit={submitPassword} className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Password attuale</span>
            <input type="password" required value={pwOld} onChange={e => setPwOld(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-[var(--border-silver)]" data-testid="pp-current" autoComplete="current-password" />
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Nuova password (min 12)</span>
            <input type="password" required value={pwNew} onChange={e => setPwNew(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-[var(--border-silver)]" data-testid="pp-new" autoComplete="new-password" />
          </label>
          <button type="submit" disabled={busy} data-testid="pp-submit"
            className="md:col-span-2 px-3 py-2 rounded-md text-white text-sm" style={{ background: "var(--brand-dark)" }}>
            {busy ? "Aggiornamento…" : "Aggiorna password"}
          </button>
        </form>
      </section>

      <section className="card-clean p-5 mb-5" data-testid="platform-profile-email">
        <div className="flex items-center gap-2 mb-3"><Mail className="w-4 h-4" /><h2 className="font-serif text-xl">Cambio email</h2></div>
        <form onSubmit={submitEmail} className="text-sm">
          <label className="block mb-3">
            <span className="text-[10px] uppercase tracking-widest text-neutral-500 block mb-1">Nuova email</span>
            <input type="email" required value={newEmail} onChange={e => setNewEmail(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-[var(--border-silver)]" data-testid="pe-new" />
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <input type="password" required placeholder="Password attuale" value={emPw} onChange={e => setEmPw(e.target.value)} autoComplete="current-password"
              className="w-full px-3 py-2 rounded-md border border-[var(--border-silver)]" data-testid="pe-password" />
            {user?.mfa_enabled && <input required placeholder="Codice a 6 cifre o di recupero" value={emCode} onChange={e => setEmCode(e.target.value.trim())} autoComplete="one-time-code"
              className="w-full px-3 py-2 rounded-md border border-[var(--border-silver)]" data-testid="pe-code" />}
          </div>
          <button type="submit" disabled={busy || !newEmail || !emPw || (user?.mfa_enabled && !emCode)} data-testid="pe-submit"
            className="px-3 py-2 rounded-md border border-[var(--brand-primary)] text-sm" style={{ color: "var(--brand-dark)" }}>
            Richiedi cambio email
          </button>
          <p className="text-[10px] text-neutral-500 mt-2">
            Ti verrà inviato un link di conferma alla nuova email.
          </p>
        </form>
      </section>

      <section className="card-clean p-5" data-testid="platform-profile-sessions">
        <div className="flex items-center gap-2 mb-3"><UserCog className="w-4 h-4" /><h2 className="font-serif text-xl">Sessioni</h2></div>
        <button onClick={submitLogoutAll} data-testid="pp-logout-all"
          className="px-3 py-2 rounded-md border border-rose-400 text-rose-700 text-sm">
          Esci da tutti i dispositivi
        </button>
      </section>
      <MfaSettings />
    </div>
  );
}
